// app/javascript/components/quotes/builder/components/AggregateBlock.js
import React, { useState, useRef, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import LongDescription from "./LongDescription"
import RowMenu from "./RowMenu"
import { useDecimalDraft } from "./DecimalInput"
import AddArticleModal from "./AddArticleModal"
import { useRowEditor } from "../context/QuoteEditorContext"
import { useGroupPicker } from "../context/GroupPickerContext"
import { hasLiveChildren, isOption, nodeTotals } from "../helpers/treeMath"
import { formatNumber } from "../helpers/formatting"
import { makeLine } from "../helpers/newLine"
import Input      from "@react/shared/ui/Input"
import Badge      from "@react/shared/ui/Badge"
import Button     from "@react/shared/ui/Button"
import IconBadge  from "@react/shared/ui/IconBadge"
import IconButton from "@react/shared/ui/IconButton"
import DragHandle from "@react/shared/ui/DragHandle"
import { TreeLead, TreeStem } from "@react/shared/ui/TreeGuides"

const marginTone = pct => (pct >= 25 ? "text-emerald-600" : pct >= 15 ? "text-amber-600" : "text-rose-600")

const TYPE_ROW = {
  group:    "bg-white",
  subgroup: "bg-slate-50/40",
}

const TITLE_FONT = {
  group:    "text-[14.5px] font-semibold -tracking-[0.01em]",
  subgroup: "text-[13.5px] font-medium",
}

const TOTAL_TONE = {
  group:    "text-section-500 text-[14px] font-bold",
  subgroup: "text-subsection-500 font-semibold",
}

const REF_TAG_TONE    = { group: "section", subgroup: "subsection" }
const REF_TAG_VARIANT = { group: "solid",   subgroup: "soft"       }

const CELL_END    = "flex items-center min-w-0 row-start-1 justify-end"
const CELL_CENTER = "flex items-center min-w-0 row-start-1 justify-center"
const STATIC      = "py-1 tabular-nums text-slate-500 text-[12.5px] whitespace-nowrap"

const AggregateBlock = ( { node, variant, code, depth = 0, trail = [], last = false, children }) => {
  const { settings, dispatch, getState } = useRowEditor()
  const { open: openGroupPicker } = useGroupPicker()

  const canAddChildren = variant === "group" || variant === "subgroup"
  const subLabel = depth === 0
    ? I18n.t("quotes.builder.aggregate.subgroup")
    : I18n.t("quotes.builder.aggregate.sub_subgroup")

  const addSubsection = () => {
    dispatch({
      type: "ADD_SUBSECTION",
      payload: {
        parentUid: node._uid,
        data: { type: "group", description: "", children: [] },
      },
    })
  }
  const addGroupAfter = () => {
    dispatch({
      type: "ADD_SECTION",
      payload: {
        type:          "group",
        description:   "",
        children:      [],
        _expanded:     true,
        insertAfterUid: node._uid,
      },
    })
  }
  const addGroup = () => openGroupPicker(node._uid)
  const addLine = (costCategory = "materials") => {
    dispatch({
      type: "ADD_LINES_TO_SECTION",
      payload: {
        sectionUid: node._uid,
        focusLast:  true,
        lines: [makeLine(getState(), costCategory)],
      },
    })
  }

  const addDescription = () =>
    dispatch({ type: "ADD_DESCRIPTION", payload: { parentUid: node._uid } })

  const [articleModalOpen, setArticleModalOpen] = useState(false)
  const [discountOpen,     setDiscountOpen]     = useState(false)
  const discountRef = useRef(null)

  useEffect(() => {
    if (discountOpen && discountRef.current) {
      discountRef.current.focus()
      discountRef.current.select()
    }
  }, [discountOpen])
  const addArticleFromProduct = data => {
    dispatch({
      type: "ADD_LINES_TO_SECTION",
      payload: {
        sectionUid: node._uid,
        focusLast:  true,
        lines: [{
          type:             "service",
          description:         data.name || "",
          cost_category:       data.cost_category || "materials",
          quantity:            1,
          unit:                data.unit || "",
          unit_price:          data.sale_price ?? 0,
          purchase_price:      data.purchase_price ?? null,
          discount:            0,
          coefficient:         1,
          ref_product_distrib: data.ref_product_brand || "",
          product_id:          data.id,
          vat_rate:            settings.site?.vat_rate || {},
          currency:            settings.currency || {},
          currency_symbol:     settings.currencySymbol || "€",
        }],
      },
    })
  }

  const { total, cost, unitSale, unitCost } = nodeTotals(node)
  const option = isOption(node)
  const margin = total > 0 ? ((total - cost) / total) * 100 : 0
  const avgCoef = cost > 0 ? total / cost : 1

  const vatRates      = settings.account?.vat_rates || []
  const currentVatId  = node.vat_rate?.id
  const currentVatPct = node.vat_rate?.percentage

  const descHtml = (node.description && node.description.trim()) ? node.description : ""

  const hasChildren   = (node.children || []).some(c => c.type === "group")
  const hasComponents = (node.children || []).some(c => c.type === "service")
                      || (node.components?.length || 0) > 0
  const hasDescriptions = (node.children || []).some(c => c.type === "descriptive" && !c._destroy)
  const hasAnyComponents = n =>
    (n.children || []).some(c => c.type === "service") ||
    (n.components?.length || 0) > 0 ||
    (n.children || []).filter(c => c.type === "group").some(hasAnyComponents)
  const showTotals = hasAnyComponents(node)

  const [editing, setEditing] = useState(!!node._focusDescription)
  const [draft, setDraft]     = useState(node._focusDescription ? "" : "")
  const inputRef              = useRef(null)

  const autoGrow = element => {
    if (!element) return
    element.style.height = "auto"
    element.style.height = `${element.scrollHeight}px`
  }

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
      autoGrow(inputRef.current)
    }
  }, [editing])

  useEffect(() => {
    if (node._focusDescription) {
      dispatch({ type: "UPDATE_NODE", payload: { nodeUid: node._uid, fields: { _focusDescription: false } } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const startEdit  = e => {
    if (editing) return
    e?.stopPropagation?.()
    setDraft(toPlainText(descHtml))
    setEditing(true)
  }
  const commitEdit = () => {
    dispatch({ type: "UPDATE_NODE", payload: { nodeUid: node._uid, field: "description", value: toDescriptionHtml(draft) } })
    setEditing(false)
  }
  const cancelEdit = () => { setDraft(""); setEditing(false) }

  const onTwistClick = e => {
    e.stopPropagation()
    dispatch({ type: "TOGGLE_NODE", payload: { nodeUid: node._uid } })
  }

  const open  = hasLiveChildren(node) && node._expanded !== false
  const twist = (
    <IconButton
      size       = "xs"
      data-twist = ""
      onClick    = {onTwistClick}
      title      = {I18n.t("quotes.builder.aggregate.toggle_collapse")}
      className  = "shrink-0 [&_svg]:transition-transform [&_svg]:text-[10px]"
    >
      <FontAwesomeIcon icon="chevron-down" />
    </IconButton>
  )

  const isGroup = variant === "group"
  const qty  = node.quantity != null ? parseFloat(node.quantity) : ""
  const unit = node.unit || ""

  const onQtyChange = e => {
    const raw    = e.target.value
    const newQty = parseFloat(String(raw).replace(",", ".")) || 0
    dispatch({ type: "UPDATE_NODE", payload: { nodeUid: node._uid, fields: { quantity: raw === "" ? null : newQty } } })
  }
  const discount = parseFloat(node.discount) || 0
  const showDiscount = hasComponents && (discount > 0 || discountOpen)
  const openDiscount = () => setDiscountOpen(true)
  const removeDiscount = () => {
    setDiscountOpen(false)
    dispatch({ type: "UPDATE_NODE", payload: { nodeUid: node._uid, fields: { discount: 0 } } })
  }
  const discountInput = useDecimalDraft({
    value:    discount || "",
    onChange: raw => {
      const value = Math.min(100, Math.max(0, parseFloat(raw.replace(",", ".")) || 0))
      dispatch({ type: "UPDATE_NODE", payload: { nodeUid: node._uid, fields: { discount: value } } })
    },
    onBlur:   () => { if (!(parseFloat(node.discount) > 0)) setDiscountOpen(false) },
  })
  const onUnitChange = e =>
    dispatch({ type: "UPDATE_NODE", payload: { nodeUid: node._uid, field: "unit", value: e.target.value } })
  const onVatChange = id => {
    if (!id) {
      dispatch({ type: "UPDATE_NODE", payload: { nodeUid: node._uid, fields: { vat_rate: {} } } })
      return
    }
    const vat = vatRates.find(v => String(v.id) === String(id))
    if (vat) dispatch({ type: "UPDATE_NODE", payload: { nodeUid: node._uid, fields: { vat_rate: vat } } })
  }

  return (
    <>
      <div
        data-uid       = {node._uid}
        onFocusCapture = {() => dispatch({ type: "SET_ACTIVE_SECTION", payload: { sectionUid: node._uid } })}
        className      = {`qb-grid items-center px-3 py-1.5 relative group ${option ? "bg-amber-50" : hasChildren ? "bg-slate-100" : TYPE_ROW[variant]}`}
      >
        <div className="qb-lot-tree col-start-1 col-end-3 row-start-1 flex items-stretch self-stretch min-w-0">
          <div className="flex items-center gap-1 shrink-0">
            {/* Where lines have their checkbox, so every row's tree starts at the same place. */}
            <span className="w-3.5 shrink-0" aria-hidden="true" />
            <DragHandle
              reveal    = "onHover"
              size      = "sm"
              icon      = "bars"
              className = "!opacity-0 group-hover:!opacity-100 shrink-0"
            />
          </div>

          <div className="flex items-stretch shrink-0 ml-1">
            <TreeLead depth={depth} trail={trail} last={last} toggle={(hasChildren || hasComponents || hasDescriptions) ? twist : null} />
            <span className="relative shrink-0 flex items-center">
              <IconBadge tone={isGroup ? "section" : "subsection"} size="sm" title={I18n.t(isGroup ? "quotes.builder.col_head.add_group" : "quotes.builder.aggregate.subgroup")}>
                <FontAwesomeIcon icon="layer-group" />
              </IconBadge>
              {open && <TreeStem />}
            </span>
          </div>

          <div className="qb-lot-label flex-1 min-w-0 flex flex-col justify-center gap-0.5 pl-5">
            <div className="flex items-center gap-2 cursor-text" onClick={startEdit}>
              {code && (
                <span className="shrink-0 font-mono text-[11px] text-slate-500 tabular-nums tracking-tight">
                  {code}
                </span>
              )}

              {node.ref_product_distrib && (
                <Badge tone="neutral" variant="soft" size="xs" className="font-mono tracking-[0.02em]" title={node.ref_product_distrib}>
                  {node.ref_product_distrib}
                </Badge>
              )}

              {editing ? (
                <textarea
                  ref         = {inputRef}
                  rows        = {1}
                  value       = {draft}
                  placeholder = {fallbackLabel(variant)}
                  onChange    = {e => {
                    const next = e.target.value
                    setDraft(next)
                    autoGrow(e.target)
                    dispatch({ type: "UPDATE_NODE", payload: { nodeUid: node._uid, field: "description", value: toDescriptionHtml(next) } })
                  }}
                  onBlur      = {commitEdit}
                  onKeyDown   = {e => {
                    if (e.key === "Escape") { e.preventDefault(); cancelEdit() }
                  }}
                  className   = {`flex-1 min-w-0 border-0 bg-transparent outline-none p-0 resize-none overflow-hidden whitespace-normal break-words text-slate-900 ${TITLE_FONT[variant]}`}
                />
              ) : (
                <span
                  onClick   = {startEdit}
                  title     = {I18n.t("quotes.builder.aggregate.click_to_edit_title")}
                  className = {`flex-1 min-w-0 whitespace-pre-line break-words text-slate-900 cursor-text ${TITLE_FONT[variant]}`}
                >
                  {toPlainText(descHtml) || fallbackLabel(variant)}
                </span>
              )}

              {option && (
                <Badge tone="warning" variant="soft" size="xs" className="shrink-0">
                  {I18n.t("qline_groups.form.is_option")}
                </Badge>
              )}
            </div>

            <LongDescription node={node} />
          </div>
        </div>

        <div className={`${CELL_END} col-start-3`}>
          {!hasChildren && (
            <input
              type      = "number"
              step      = "0.01"
              min       = "0"
              value     = {qty}
              onChange  = {onQtyChange}
              title     = {I18n.t("quotes.builder.aggregate.quantity_tooltip")}
              className = "w-full h-7 text-[12.5px] tabular-nums text-right border-0 bg-transparent outline-none focus:bg-white rounded text-slate-900"
            />
          )}
        </div>
        <div className={`${CELL_CENTER} col-start-4`}>
          {!hasChildren && (
            <input
              type        = "text"
              value       = {unit}
              onChange    = {onUnitChange}
              placeholder = {I18n.t("quotes.builder.aggregate.unit_placeholder")}
              className   = "w-full h-7 text-[12.5px] text-center border-0 bg-transparent outline-none focus:bg-white rounded text-slate-700 placeholder:text-slate-300"
            />
          )}
        </div>

        <div className={`${CELL_END} qb-col-purchase col-start-5`}>
          <span className={STATIC}>{unitCost ? `${formatNumber(unitCost)} ${settings.currencySymbol}` : "—"}</span>
        </div>

        <div className={`${CELL_END} qb-col-coef col-start-6`}>
          {showTotals && (
            <span className={STATIC}>
              {avgCoef ? (Math.round(avgCoef * 100) / 100).toString().replace(".", ",") : "—"}
            </span>
          )}
        </div>

        <div className={`${CELL_END} col-start-7`}>
          <span className={STATIC}>{unitSale ? `${formatNumber(unitSale)} ${settings.currencySymbol}` : "—"}</span>
        </div>

        <div className={`${CELL_END} qb-col-margin col-start-8`}>
          <span className={`${STATIC} font-semibold ${cost && total > 0 ? marginTone(margin) : ""}`}>
            {cost && total > 0 ? `${margin >= 0 ? "+" : ""}${margin.toFixed(1)} %` : "—"}
          </span>
        </div>

        <div className={`${CELL_CENTER} col-start-9`}>
          {hasComponents ? (
            <div className="relative inline-flex items-center justify-center w-full px-1 py-0.5 rounded-md border border-transparent hover:border-slate-200 focus-within:border-brand-500 focus-within:ring-[3px] focus-within:ring-brand-500/20 focus-within:bg-white">
              <span className="text-[11.5px] text-slate-700 tabular-nums pointer-events-none">
                {currentVatPct != null ? `${currentVatPct} %` : "—"}
              </span>
              <select
                value     = {currentVatId || ""}
                disabled  = {settings.readOnlyVat}
                required
                data-vat-required = "true"
                onChange  = {e => {
                  e.target.setCustomValidity("")
                  onVatChange(e.target.value)
                }}
                onInvalid = {e => e.target.setCustomValidity(I18n.t("quotes.builder.aggregate.select_vat"))}
                title     = {node.vat_rate?.label || ""}
                className = "absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none"
              >
                <option value="">—</option>
                {vatRates.map(v => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </select>
            </div>
          ) : (
            <span className={`${STATIC} text-slate-400 italic`}>—</span>
          )}
        </div>

        <div className={`${CELL_END} col-start-10 flex-col !items-end`}>
          {showTotals && (
            <span className={`py-1 tabular-nums whitespace-nowrap ${option ? "text-slate-400 italic font-semibold" : TOTAL_TONE[variant]}`}>{formatNumber(total)} {settings.currencySymbol}</span>
          )}
          {showDiscount && (
            <label
              title     = {I18n.t("quotes.builder.aggregate.discount_tooltip")}
              className = "inline-flex items-center gap-1 text-[11px] tabular-nums rounded px-1 text-emerald-600 focus-within:bg-white focus-within:ring-1 focus-within:ring-brand-500/40"
            >
              <span className="text-slate-500">{I18n.t("quotes.builder.aggregate.discount_label")}</span>
              <span aria-hidden="true">−</span>
              <input
                ref       = {discountRef}
                {...discountInput}
                placeholder = "0"
                aria-label = {I18n.t("quotes.builder.aggregate.discount_tooltip")}
                style     = {{ width: `${Math.max(discountInput.value.length, 1) + 0.5}ch` }}
                className = "h-5 text-right border-0 bg-transparent outline-none p-0 tabular-nums"
              />
              <span aria-hidden="true">%</span>
            </label>
          )}
        </div>

        <div className="col-start-11 row-start-1 flex items-center justify-end">
          <RowMenu
            node                = {node}
            onAddSiblingGroup   = {depth === 0 ? addGroupAfter : null}
            onAddSubsection     = {canAddChildren && !hasComponents && (hasChildren || !hasDescriptions) ? addSubsection : null}
            onAddGroup          = {canAddChildren && !hasChildren   ? addGroup      : null}
            onAddLine           = {canAddChildren && !hasChildren   ? addLine       : null}
            onAddArticle        = {canAddChildren && !hasChildren   ? () => setArticleModalOpen(true) : null}
            onAddDescription    = {canAddChildren && !hasChildren   ? addDescription : null}
            onAddDiscount       = {hasComponents && !(discount > 0) ? openDiscount   : null}
            onRemoveDiscount    = {hasComponents && discount > 0    ? removeDiscount : null}
            subLabel            = {subLabel.toLowerCase()}
          />
        </div>
      </div>

      <AddArticleModal
        isOpen   = {articleModalOpen}
        onClose  = {() => setArticleModalOpen(false)}
        clientId = {settings.client?.id}
        onSelect = {addArticleFromProduct}
      />

      {children}
    </>
  )
}

const fallbackLabel = type =>
  type === "group"    ? I18n.t("quotes.builder.aggregate.new_group")    :
  type === "subgroup" ? I18n.t("quotes.builder.aggregate.new_subgroup") :
                        I18n.t("quotes.builder.aggregate.new_service")

const escapeHtml = s =>
  (s || "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))

const toPlainText = (html) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html")
  doc.body.querySelectorAll("br").forEach(br => br.replaceWith("\n"))
  return (doc.body.textContent || "").replace(/\u00a0/g, " ").trim()
}

const toDescriptionHtml = (text) => {
  const trimmed = (text || "").trim()
  return trimmed ? `<p>${escapeHtml(trimmed).replace(/\n/g, "<br>")}</p>` : ""
}

export default AggregateBlock
